import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Share2, Lock, ChevronRight, Hourglass } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { signupUrl } from "../lib/claimReport";
import ShareSheet, { useShareCard } from "./ShareSheet";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LORD_COLOR = {
  Sun: "#FFD166", Moon: "#c084fc", Mars: "#fca5a5", Rahu: "#67e8f9", Jupiter: "#fdba74",
  Saturn: "#93c5fd", Mercury: "#86efac", Ketu: "#f9a8d4", Venus: "#f0abfc",
};

const STATE_LABEL = {
  en: { past: "Behind you", current: "You are here", ahead: "Ahead" },
  hi: { past: "बीत चुका", current: "आप यहाँ हैं", ahead: "आगे" },
};

// Your chart as chapters you can tap through, instead of a wall of predictions.
export const LifeTimeline = ({ chart, reportId, name, birth, summary }) => {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);
  const [failed, setFailed] = useState(false);
  const { card, busy, error: shareError, create, close } = useShareCard(API);

  useEffect(() => {
    if (!chart && !reportId) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    axios.post(`${API}/kundli/timeline`, {
      chart_json: chart || undefined, report_id: reportId || undefined, name: name || "",
      birth_year: birth?.year, birth_month: birth?.month, birth_day: birth?.day, lang,
    }, { withCredentials: true })
      .then(r => { if (alive) { setFailed(false); setData(r.data); setOpenIdx((r.data.chapters || []).findIndex(c => c.state === "current")); } })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [chart, reportId, lang, name, birth?.year, birth?.month, birth?.day, user]);

  if (loading) {
    return (
      <div data-testid="timeline-loading" className="glass-card" style={{ marginBottom: "18px", padding: "16px 18px" }}>
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.88rem", margin: 0 }}>
          {lang === "hi" ? "आपकी जीवन-रेखा बना रहे हैं…" : "Building your Life Timeline…"}
        </p>
      </div>
    );
  }
  if (!data?.chapters?.length) {
    if (!failed) return null;
    return (
      <div data-testid="timeline-error" className="glass-card" style={{ marginBottom: "18px", padding: "14px 16px", border: "1px solid rgba(252,165,165,0.3)" }}>
        <p style={{ color: "rgba(252,165,165,0.9)", fontSize: "0.85rem", margin: 0, lineHeight: 1.6 }}>
          {lang === "hi" ? "जीवन-रेखा अभी नहीं बन सकी — कुछ देर में फिर देखें।" : "Your Life Timeline couldn't be built just now — please try again in a moment."}
        </p>
      </div>
    );
  }

  const chapters = data.chapters;
  const current = chapters.find(c => c.state === "current") || chapters[0];
  const L = STATE_LABEL[lang === "hi" ? "hi" : "en"];

  const share = () => create({
    kind: "timeline",
    name: name || "",
    headline: `${current.lord} chapter · ages ${current.age_from ?? "?"}-${current.age_to ?? "?"}`,
    title: current.title || "",
    moon_sign: summary?.moon_sign || summary?.moonSign || "",
    lagna: summary?.lagna || summary?.ascendant || "",
    rows: chapters.slice(0, 5).map(c => `${c.lord} · ${c.age_from != null ? `${c.age_from}-${c.age_to}` : `${c.year_from}-${c.year_to}`}`),
    current_row: Math.max(0, chapters.findIndex(c => c.state === "current")),
  });

  return (
    <div data-testid="life-timeline" className="glass-card animate-fadeInUp" style={{
      marginBottom: "18px", padding: "16px 18px",
      border: "1px solid rgba(103,232,249,0.26)",
      background: "linear-gradient(135deg, rgba(103,232,249,0.07), rgba(157,78,221,0.08))",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", marginBottom: "4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Hourglass size={15} style={{ color: "#67e8f9" }} />
          <span className="font-cinzel" style={{ color: "#67e8f9", fontSize: "0.98rem" }}>
            {lang === "hi" ? "आपकी जीवन-रेखा" : "Your Life Timeline"}
          </span>
        </div>
        <button data-testid="timeline-share-btn" onClick={share} disabled={busy} style={{
          display: "inline-flex", alignItems: "center", gap: "6px", cursor: busy ? "default" : "pointer",
          background: "rgba(255,209,102,0.12)", border: "1px solid rgba(255,209,102,0.4)",
          color: "#FFD166", borderRadius: "40px", padding: "7px 14px", fontSize: "0.78rem", fontWeight: 700,
          opacity: busy ? 0.6 : 1,
        }}>
          <Share2 size={13} /> {busy ? (lang === "hi" ? "बना रहे हैं…" : "Creating…") : (lang === "hi" ? "शेयर करें" : "Share")}
        </button>
      </div>
      {shareError && (
        <p data-testid="timeline-share-error" style={{ color: "rgba(252,165,165,0.9)", fontSize: "0.78rem", margin: "0 0 8px" }}>
          {shareError}
        </p>
      )}
      <p style={{ color: "rgba(255,255,255,0.52)", fontSize: "0.79rem", margin: "0 0 14px", lineHeight: 1.6 }}>
        {lang === "hi"
          ? "आपकी असली दशा अवधियों से बनी — किसी भी अध्याय पर टैप करें।"
          : "Built from your real Dasha periods — tap any chapter to open it."}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {chapters.map((c, i) => {
          const colour = LORD_COLOR[c.lord] || "#c084fc";
          const isOpen = openIdx === i;
          const locked = !!c.is_locked;
          return (
            <div key={`${c.lord}-${c.year_from}`} data-testid={`timeline-chapter-${i}`} style={{
              border: `1px solid ${c.state === "current" ? `${colour}66` : "rgba(255,255,255,0.09)"}`,
              borderRadius: "12px", overflow: "hidden",
              background: c.state === "current" ? `${colour}12` : "rgba(255,255,255,0.03)",
            }}>
              <button data-testid={`timeline-chapter-btn-${i}`}
                onClick={() => setOpenIdx(isOpen ? null : i)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "11px", textAlign: "left",
                  background: "none", border: "none", cursor: "pointer", padding: "12px 13px", color: "#fff",
                }}>
                <span style={{
                  width: "8px", height: "38px", borderRadius: "6px", flexShrink: 0,
                  background: c.state === "past" ? "rgba(255,255,255,0.18)" : colour,
                }} />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: "block", color: colour, fontWeight: 700, fontSize: "0.9rem" }}>
                    {c.lord} {lang === "hi" ? "दशा" : "period"}
                    {locked && <Lock size={11} style={{ marginLeft: "6px", verticalAlign: "-1px", color: "rgba(255,255,255,0.45)" }} />}
                  </span>
                  <span style={{ display: "block", color: "rgba(255,255,255,0.52)", fontSize: "0.76rem", marginTop: "2px" }}>
                    {c.age_from != null ? `${lang === "hi" ? "आयु" : "Age"} ${c.age_from}–${c.age_to} · ` : ""}
                    {c.year_from}–{c.year_to}
                    {c.state === "current" && <strong style={{ color: colour }}> · {L.current}</strong>}
                    {c.state === "past" && ` · ${L.past}`}
                  </span>
                </span>
                <ChevronRight size={16} style={{
                  flexShrink: 0, color: "rgba(255,255,255,0.35)",
                  transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .2s",
                }} />
              </button>

              {isOpen && (
                <div style={{ padding: "0 13px 13px 32px" }}>
                  {locked ? (
                    <div data-testid={`timeline-locked-${i}`}>
                      {c.placement && (
                        <p data-testid={`timeline-placement-${i}`} style={{ color: "#67e8f9", fontSize: "0.79rem", lineHeight: 1.6, margin: "0 0 7px" }}>
                          {c.placement}
                        </p>
                      )}
                      <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.84rem", lineHeight: 1.7, margin: "0 0 10px" }}>
                        {lang === "hi"
                          ? "यह अध्याय मुफ़्त खाते के साथ खुलता है — कोई कार्ड नहीं।"
                          : "This chapter opens with a free account — no card needed."}
                      </p>
                      <button data-testid={`timeline-unlock-btn-${i}`} className="btn-gold"
                        onClick={() => navigate(signupUrl("/kundli/report"))}
                        style={{ padding: "9px 18px", fontSize: "0.82rem" }}>
                        {lang === "hi" ? "मुफ़्त में खोलें" : "Unlock free"}
                      </button>
                    </div>
                  ) : (
                    <>
                      {c.title && (
                        <p data-testid={`timeline-title-${i}`} style={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem", margin: "0 0 6px" }}>
                          {c.title}
                        </p>
                      )}
                      {c.placement && (
                        <p data-testid={`timeline-placement-${i}`} style={{ color: "#67e8f9", fontSize: "0.79rem", lineHeight: 1.6, margin: "0 0 7px" }}>
                          {c.placement}
                        </p>
                      )}
                      <p data-testid={`timeline-summary-${i}`} style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.86rem", lineHeight: 1.75, margin: 0 }}>
                        {c.summary}
                      </p>
                      {c.action && (
                        <p data-testid={`timeline-action-${i}`} style={{ color: "#FFD166", fontSize: "0.82rem", lineHeight: 1.65, margin: "9px 0 0" }}>
                          ✦ {c.action}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {data.locked > 0 && (
        <p data-testid="timeline-locked-note" style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.76rem", marginTop: "12px", lineHeight: 1.6 }}>
          {lang === "hi"
            ? `${data.locked} और अध्याय — मुफ़्त खाते से खुलते हैं।`
            : `${data.locked} more chapter${data.locked > 1 ? "s" : ""} — free account unlocks them.`}
        </p>
      )}

      {/* Everything above is a summary — the depth lives in the report and the chat */}
      <div data-testid="timeline-report-cta" style={{
        marginTop: "14px", paddingTop: "13px", borderTop: "1px solid rgba(255,255,255,0.08)",
      }}>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.79rem", lineHeight: 1.65, margin: "0 0 10px" }}>
          {lang === "hi"
            ? "यह सिर्फ़ झलक है। अपनी पूरी निजी कुंडली रिपोर्ट नीचे पढ़ें, और इसी चरण पर सवाल पूछें।"
            : "This is the short version. Your full personalised Kundli report is below — read it, then ask your own question about this phase."}
        </p>
        <div style={{ display: "flex", gap: "9px", flexWrap: "wrap" }}>
          <button data-testid="timeline-open-report-btn" className="btn-gold" style={{ padding: "9px 17px", fontSize: "0.82rem" }}
            onClick={() => { navigate("/kundli/report"); window.scrollTo({ top: 700, behavior: "smooth" }); }}>
            {lang === "hi" ? "पूरी रिपोर्ट पढ़ें" : "Read my full report"}
          </button>
          <button data-testid="timeline-ask-btn" className="btn-ghost" style={{ padding: "9px 17px", fontSize: "0.82rem" }}
            onClick={() => navigate("/chat")}>
            {lang === "hi" ? "इस चरण पर सवाल पूछें" : "Ask about this phase"}
          </button>
        </div>
      </div>

      {card && <ShareSheet card={card} onClose={close} />}
    </div>
  );
};

export default LifeTimeline;
